<template>
  <img
    ref="imgRef"
    :data-src="actualSrc"
    :alt="alt"
    :class="[className, { 'lazy-loading': isLoading, 'lazy-loaded': isLoaded }]"
    :style="computedStyle"
    @load="handleLoad"
    @error="handleError"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// 默认图片
const defaultImage = require('@/assets/image/no_image.png')

const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  alt: {
    type: String,
    default: ''
  },
  className: {
    type: String,
    default: ''
  },
  style: {
    type: Object,
    default: () => ({})
  },
  fit: {
    type: String,
    default: 'cover'
  }
})

// 计算实际显示的图片地址
const actualSrc = computed(() => {
  return props.src || defaultImage
})

const computedStyle = computed(() => {
  const fitStyles = {
    fill: { objectFit: 'fill' },
    contain: { objectFit: 'contain' },
    cover: { objectFit: 'cover' },
    none: { objectFit: 'none' },
    'scale-down': { objectFit: 'scale-down' }
  }
  return {
    ...props.style,
    ...(fitStyles[props.fit] || fitStyles.cover),
    objectPosition: 'center'
  }
})

const imgRef = ref(null)
const isLoading = ref(true)
const isLoaded = ref(false)
let observer = null

const handleLoad = () => {
  isLoading.value = false
  isLoaded.value = true
}

const handleError = () => {
  isLoading.value = false
  // 图片加载失败时，显示默认图片
  if (imgRef.value && imgRef.value.src !== defaultImage) {
    imgRef.value.src = defaultImage
  }
}

const loadImage = () => {
  if (imgRef.value && !isLoaded.value) {
    const src = imgRef.value.getAttribute('data-src')
    if (src) {
      imgRef.value.src = src
    }
  }
}

onMounted(() => {
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadImage()
            observer.unobserve(imgRef.value)
          }
        })
      },
      {
        rootMargin: '100px',
        threshold: 0.1
      }
    )

    if (imgRef.value) {
      observer.observe(imgRef.value)
    }
  } else {
    // 降级方案：直接加载
    loadImage()
  }
})

onUnmounted(() => {
  if (observer && imgRef.value) {
    observer.unobserve(imgRef.value)
  }
})
</script>

<style lang="scss" scoped>
img {
  display: block;
}

.lazy-loading {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.lazy-loaded {
  opacity: 1;
}
</style>